import { useState, type ChangeEvent, type FormEvent } from "react";
import { Auth } from "aws-amplify";
import type { CognitoUser } from "amazon-cognito-identity-js";
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  CssBaseline,
  LinearProgress,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AccountPreRegistration from "./pages/Account/AccountPreRegistration";

type SignInView =
  | "signIn"
  | "accountPreRegistration"
  | "confirmSignInForNewPassword"
  | "setNewPassword"
  | "confirmSignIn"
  | "resetPassword"
  | "setPassword";

type AuthErrorLike = {
  code?: string;
};

type CustomSignInProps = {
  onSignIn: (user: CognitoUser) => void;
};

const authErrorMessage = {
  UserNotConfirmedException: "ユーザーが未確認です。",
  PasswordResetRequiredException: "パスワードのリセットが必要です。",
  UserNotFoundException: "このメールアドレスは登録されていません。",
  InvalidParameterException: "入力内容に誤りがあります。ご確認ください。",
  ExpiredCodeException: "認証コードの有効期限が切れています。",
  CodeMismatchException: "認証コードが正しくありません。",
  LimitExceededException:
    "操作が制限されました。しばらく時間を置いて再試行してください。",
  InvalidPasswordException:
    "パスワードは8文字以上で、少なくとも1つの数字、1つの特殊文字、1つの大文字、1つの小文字を含めてください",
  notAuthorizedByFlow: {
    signIn: "メールアドレスまたはパスワードが正しくありません。",
    completeNewPassword:
      "認証の有効期限が切れています。サインインからやり直してください。",
    customChallenge: "認証に失敗しました。もう一度お試しください。",
  },
} as const;

function getAuthErrorMessage(
  error: AuthErrorLike,
  fallbackMessage: string,
  contextOverrides: Record<string, string> = {}
): string {
  const code = error?.code;
  if (!code || typeof code !== "string") {
    return fallbackMessage;
  }
  if (Object.prototype.hasOwnProperty.call(contextOverrides, code)) {
    return contextOverrides[code];
  }
  if (Object.prototype.hasOwnProperty.call(authErrorMessage, code)) {
    const message = authErrorMessage[code as keyof typeof authErrorMessage];
    if (typeof message === "string") {
      return message;
    }
  }
  return fallbackMessage;
}

const CustomSignIn = ({ onSignIn }: CustomSignInProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signInUser, setSignInUser] = useState<CognitoUser | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentView, setCurrentView] = useState<SignInView>("signIn");

  const handleSignIn = async (event: FormEvent<HTMLFormElement>) => {
    setLoading(true);
    setError("");
    try {
      event.preventDefault();
      const user = await Auth.signIn(email, password);
      setSignInUser(user);
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        console.log(
          "NEW_PASSWORD_REQUIRED: 新しいパスワードを入力してください。"
        );
        setCurrentView("setNewPassword");
      } else if (user.challengeName === "CUSTOM_CHALLENGE") {
        setCurrentView("confirmSignIn");
      } else {
        onSignIn(user);
      }
    } catch (signInError) {
      console.error("Error signing in", signInError);
      setError(
        getAuthErrorMessage(
          signInError as AuthErrorLike,
          "サインインに失敗しました。再試行してください。",
          {
            NotAuthorizedException:
              authErrorMessage.notAuthorizedByFlow.signIn,
          }
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteNewPassword = async (event: FormEvent<HTMLFormElement>) => {
    setLoading(true);
    setError("");
    try {
      event.preventDefault();
      if (newPassword !== confirmPassword) {
        setError("パスワードが一致しません。");
        return;
      }
      if (!signInUser) {
        setError("サインインセッションが無効です。再度サインインしてください。");
        return;
      }
      const updatedUser = await Auth.completeNewPassword(
        signInUser,
        newPassword
      );
      onSignIn(updatedUser);
    } catch (passwordError) {
      console.error("Error completing new password", passwordError);
      setCurrentView("signIn");
      setError(
        getAuthErrorMessage(
          passwordError as AuthErrorLike,
          "新しいパスワードの設定に失敗しました。再試行してください。",
          {
            NotAuthorizedException:
              authErrorMessage.notAuthorizedByFlow.completeNewPassword,
          }
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setError("");
    try {
      if (!email) {
        setError("メールアドレスを入力してください");
        setLoading(false);
        return;
      }

      setCurrentView("resetPassword");
    } catch (forgotError) {
      console.error("Error initiating forgot password", forgotError);
      setError(
        getAuthErrorMessage(
          forgotError as AuthErrorLike,
          "メールアドレスを入力してください"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignInForNewPassword = async (
    event: FormEvent<HTMLFormElement>
  ) => {
    setLoading(true);
    setError("");
    try {
      event.preventDefault();

      if (!code) {
        setError("コードが入力されていません");
        return;
      }

      await Auth.confirmSignUp(email, code);
      console.log("認証コード確認成功。パスワード変更画面へ遷移。");
      setCurrentView("setNewPassword");
    } catch (confirmError) {
      console.error("Error confirming sign in", confirmError);
      setError(
        getAuthErrorMessage(
          confirmError as AuthErrorLike,
          "確認コードの送信に失敗しました。再試行してください。"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignIn = async (event: FormEvent<HTMLFormElement>) => {
    setLoading(true);
    setError("");
    try {
      event.preventDefault();
      if (!signInUser) {
        setError("サインインセッションが無効です。再度サインインしてください。");
        return;
      }
      await Auth.sendCustomChallengeAnswer(signInUser, code);
      const user = await Auth.currentAuthenticatedUser();
      onSignIn(user);
    } catch (confirmError) {
      console.error("Error confirming sign in", confirmError);
      setError(
        getAuthErrorMessage(
          confirmError as AuthErrorLike,
          "確認コードの送信に失敗しました。再試行してください。",
          {
            NotAuthorizedException:
              authErrorMessage.notAuthorizedByFlow.customChallenge,
          }
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetCode = async (event: FormEvent<HTMLFormElement>) => {
    setLoading(true);
    setError("");
    try {
      event.preventDefault();
      await Auth.forgotPassword(email);
      setCurrentView("setPassword");
      console.log(`認証コードが ${email} 宛に送信されました。`);
    } catch (resetError) {
      console.error("Error sending reset code", resetError);
      setError(
        getAuthErrorMessage(
          resetError as AuthErrorLike,
          "コードの送信に失敗しました。再試行してください。"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (event: FormEvent<HTMLFormElement>) => {
    setLoading(true);
    setError("");
    try {
      event.preventDefault();

      if (!code) {
        setError("コードが入力されていません");
        return;
      }

      if (newPassword !== confirmPassword) {
        setError("パスワードが一致しません。");
        return;
      }

      await Auth.forgotPasswordSubmit(email, code, newPassword);
      setCurrentView("signIn");
      console.log(`パスワードが ${email} でリセットされました。`);
    } catch (setPasswordError) {
      console.error("Error setting new password", setPasswordError);
      setError(
        getAuthErrorMessage(
          setPasswordError as AuthErrorLike,
          "パスワードの設定に失敗しました。再試行してください。"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError("");
    try {
      await Auth.forgotPassword(email);
      console.log(`再送コードが ${email} 宛に送信されました。`);
    } catch (resendError) {
      console.error("Error resending code", resendError);
      setError(
        getAuthErrorMessage(
          resendError as AuthErrorLike,
          "コードの再送信に失敗しました。再試行してください。"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const theme = createTheme();

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const handleNewPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(event.target.value);
  };

  const handleBackToSignIn = () => {
    setCurrentView("signIn");
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div>
      {currentView === "signIn" && (
        <ThemeProvider theme={theme}>
          <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography component="h1" variant="h5">
                TAMOS災害対応システム
              </Typography>
              <Box component="form" onSubmit={handleSignIn} sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={handleEmailChange}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={handlePasswordChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 1 }}
                >
                  サインイン
                </Button>
                <Button
                  onClick={handleForgotPassword}
                  fullWidth
                  sx={{ mt: 1, mb: 0 }}
                >
                  パスワード再発行
                </Button>
                <Button
                  onClick={() => setCurrentView("accountPreRegistration")}
                  fullWidth
                  sx={{ mt: 1, mb: 1 }}
                >
                  アカウント発行依頼
                </Button>
                {loading && <LinearProgress />}
              </Box>
            </Box>
          </Container>
        </ThemeProvider>
      )}

      {currentView === "accountPreRegistration" && (
        <AccountPreRegistration onBack={handleBackToSignIn} />
      )}

      {currentView === "confirmSignInForNewPassword" && (
        <ThemeProvider theme={theme}>
          <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography component="h1" variant="h5">
                Confirm code
              </Typography>
              <Box
                component="form"
                onSubmit={handleConfirmSignInForNewPassword}
                sx={{ mt: 1 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="confirm-code"
                  label="Confirm code"
                  name="confirm-code"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2 }}
                >
                  SEND
                </Button>
                {loading && <LinearProgress />}
              </Box>
            </Box>
          </Container>
        </ThemeProvider>
      )}

      {currentView === "setNewPassword" && (
        <ThemeProvider theme={theme}>
          <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography component="h1" variant="h5">
                新しいパスワードを設定
              </Typography>
              <Box
                component="form"
                onSubmit={handleCompleteNewPassword}
                sx={{ mt: 1 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="new-password"
                  label="新しいパスワード"
                  type={showNewPassword ? "text" : "password"}
                  id="new-password"
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowNewPassword}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirm-password"
                  label="新しいパスワード（確認）"
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm-password"
                  autoComplete="off"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                >
                  パスワードを設定
                </Button>
                {loading && <LinearProgress />}
              </Box>
            </Box>
          </Container>
        </ThemeProvider>
      )}

      {currentView === "confirmSignIn" && (
        <ThemeProvider theme={theme}>
          <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography component="h1" variant="h5">
                Confirm code
              </Typography>
              <Box
                component="form"
                onSubmit={handleConfirmSignIn}
                sx={{ mt: 1 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="confirm-code"
                  label="Confirm code"
                  name="confirm-code"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2 }}
                >
                  SEND
                </Button>
                {loading && <LinearProgress />}
              </Box>
            </Box>
          </Container>
        </ThemeProvider>
      )}

      {currentView === "resetPassword" && (
        <ThemeProvider theme={theme}>
          <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography component="h1" variant="h5">
                パスワードをリセット
              </Typography>
              <Box
                component="form"
                onSubmit={handleSendResetCode}
                sx={{ mt: 1 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  disabled
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                >
                  コードを送信
                </Button>
                <Button
                  onClick={handleBackToSignIn}
                  fullWidth
                  sx={{ mt: 1, mb: 2 }}
                >
                  サインインに戻る
                </Button>
                {loading && <LinearProgress />}
              </Box>
            </Box>
          </Container>
        </ThemeProvider>
      )}

      {currentView === "setPassword" && (
        <ThemeProvider theme={theme}>
          <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography component="h1" variant="h5">
                パスワードを設定
              </Typography>
              <Box component="form" onSubmit={handleSetPassword} sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="confirm-code"
                  label="Confirm code"
                  name="confirm-code"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="new-password"
                  label="新しいパスワード"
                  type={showNewPassword ? "text" : "password"}
                  id="new-password"
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowNewPassword}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirm-password"
                  label="新しいパスワード（確認）"
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm-password"
                  autoComplete="off"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                >
                  送信
                </Button>
                <Button
                  onClick={handleResendCode}
                  fullWidth
                  sx={{ mt: 1, mb: 2 }}
                >
                  コードを再送信
                </Button>
                {loading && <LinearProgress />}
              </Box>
            </Box>
          </Container>
        </ThemeProvider>
      )}
    </div>
  );
};

export default CustomSignIn;
